import { Action, createReducer, on } from '@ngrx/store';
import { Period, Dashboard, DataObject } from '../models/core.models';
import { NomExportTransportTypes, NomObjectClass } from '../constants';
import { PresentationNormalizerService } from '../presentation-normalizer.service';

import {
  applyDefaultPeriod,
  objectChanged,
  periodChanged,
  removePeriodFromHistory,
  selectPreviousPeriod,
  togglePeriodLocked,
  updateDashboardSuccess,
} from '../actions/core.actions';
import {
  createDashboardPageSuccess,
  createExportScheduleSuccess,
  deleteDashboardPageSuccess,
  deleteExportScheduleSuccess,
  reorderDashboardPagesFail,
  reorderDashboardPagesSuccess,
  saveObjectOutputSettingsStateSuccess,
  selectDashboardPage,
  share,
  shareCancelled,
  shareFailure,
  shareSuccess,
  updateDashboardPagePeriod,
  updateDashboardPageSuccess,
  setDashboardPagesSelection,
  toggleDashboardPageSelection,
  clearDashboardPageSelection,
  updateObjectViewProperties,
} from '../actions/object.actions';
import { endOfDay, startOfWeek } from 'date-fns';

import { editFeedSuccess, loadFeedSuccess } from '../actions/feed.actions';
import { produce } from 'immer';
import { toUnix } from './../../shared/utils/date-utils';

// * helper function to find the next Page to select after deletion
function findNextPageToSelect(
  pages: any[],
  deletedPageId: number,
  currentSelectedPageId?: number,
): number | null {
  if (pages?.length === 0) return null;

  if (currentSelectedPageId && currentSelectedPageId !== deletedPageId) {
    return currentSelectedPageId;
  }

  // * Sort pages by page_order to maintain logical sequence
  const sortedPages = [...pages].sort(
    (a, b) => (a.page_order || 0) - (b.page_order || 0),
  );
  const deletedPageIndex = sortedPages.findIndex(
    (page) => page.id === deletedPageId,
  );

  if (deletedPageIndex === -1) {
    // * deleted Page not found, select first available
    return sortedPages[0]?.id || null;
  }

  // * Try to select previous page first, then next page
  if (deletedPageIndex > 0) {
    return sortedPages[deletedPageIndex - 1].id;
  } else if (deletedPageIndex < sortedPages.length - 1) {
    return sortedPages[deletedPageIndex + 1].id;
  }

  // * fallback to first page - should not happen
  return sortedPages[0]?.id || null;
}

export interface ObjectState {
  // * legacy fields - for backward compatibility (Feeds/Workspaces)
  period: Period;
  defaultPeriod: Period;
  isDefaultPeriod: boolean;
  isPeriodLocked: boolean;
  periodsHistory: Period[];

  object?: DataObject;
  isExporting?: boolean;
  activeWorkspaceId?: string;
  exportSchedulesCount: number;
  selectedDashboardPageIds: number[]; // * Multi-select for dashboard pages
}

const now = new Date();

// * Start of the week timestamp
const startOfWeekTimestamp = toUnix(startOfWeek(now));
// * End of today timestamp
const endOfTodayTimestamp = toUnix(endOfDay(now));

export const initialState: ObjectState = {
  period: {
    startTimestamp: startOfWeekTimestamp,
    endTimestamp: endOfTodayTimestamp,
  },
  isDefaultPeriod: true,
  defaultPeriod: {
    startTimestamp: startOfWeekTimestamp,
    endTimestamp: endOfTodayTimestamp,
  },
  isPeriodLocked: false,
  isExporting: false,
  periodsHistory: [],
  object: null,
  exportSchedulesCount: 0,
  selectedDashboardPageIds: [],
};

const _objectReducer = createReducer(
  initialState,
  on(periodChanged, (state, { period }) => {
    console.log(
      '%c [PERIOD] Dashboard period changed',
      'color: #2196F3; font-weight: bold;',
      {
        newPeriod: `${new Date(period.startTimestamp * 1000).toISOString().split('T')[0]} - ${new Date(period.endTimestamp * 1000).toISOString().split('T')[0]}`,
        affectsOnlyDashboard: true,
        pagePeriodsUnchanged: true,
      },
    );

    return {
      ...state,
      periodsHistory: [...state.periodsHistory, period],
      isDefaultPeriod:
        period.startTimestamp === state.defaultPeriod.startTimestamp &&
        period.endTimestamp === state.defaultPeriod.endTimestamp,
      period,
    };
  }),

  on(togglePeriodLocked, (state, { isPeriodLocked, period }) => {
    if (!isPeriodLocked) {
      return state;
    }

    return {
      ...state,
      period,
      defaultPeriod: period,
      isDefaultPeriod: true,
    };
  }),

  on(applyDefaultPeriod, (state) => {
    return {
      ...state,
      isDefaultPeriod: true,
      period: state.defaultPeriod,
    };
  }),

  on(
    selectPreviousPeriod,
    produce((draft) => {
      if (draft.periodsHistory.length === 0) return;

      const period = draft.periodsHistory.pop();

      draft.period = period;
      draft.isDefaultPeriod =
        period.startTimestamp === draft.defaultPeriod.startTimestamp &&
        period.endTimestamp === draft.defaultPeriod.endTimestamp;
    }),
  ),

  on(
    removePeriodFromHistory,
    produce((draft, { period }) => {
      draft.periodsHistory = draft.periodsHistory.filter(
        (p) =>
          p.startTimestamp !== period.startTimestamp ||
          p.endTimestamp !== period.endTimestamp,
      );
    }),
  ),

  on(
    loadFeedSuccess,
    produce((draft, { feed }) => {
      if (!feed) return;

      const { time_start, time_end } = feed.state_params;
      draft.period = {
        startTimestamp: time_start,
        endTimestamp: time_end,
      };
    }),
  ),

  on(objectChanged, (state, { object }) => {
    if (!object) return { ...initialState };
    const newState = { ...initialState, object };

    if (object.state_params) {
      const { time_start, time_end } = object.state_params;

      Object.assign(newState, {
        period: { startTimestamp: time_start, endTimestamp: time_end },
        defaultPeriod: { startTimestamp: time_start, endTimestamp: time_end },
        isDefaultPeriod: true,
      });
    }

    if (object['count_export_schedules']) {
      newState.exportSchedulesCount = object['count_export_schedules'];
    }

    if (object.workspace_id) {
      newState.activeWorkspaceId = object.workspace_id;
    }

    if (object.object_class_id === NomObjectClass.WORKSPACE) {
      newState.activeWorkspaceId = object.id;
    }

    newState.isPeriodLocked = object.is_period_locked;

    // * Dashboard-specific page handling
    if (
      object.object_class_id === NomObjectClass.DASHBOARD &&
      (object as Dashboard).pages
    ) {
      const dashboard = object as Dashboard;
      const firstPageId = dashboard.pages[0]?.id;

      // * Use normalization service to handle all presentation settings inheritance
      const normalizer = new PresentationNormalizerService();
      const normalizedDashboard = normalizer.normalizeDashboard(dashboard);

      newState.object = normalizedDashboard;

      // * Set selected_page_id if not already set
      if (!dashboard.selected_page_id && firstPageId) {
        newState.object = {
          ...newState.object,
          selected_page_id: firstPageId,
        } as Dashboard;
      }
    }

    return newState;
  }),

  // * only the count lives here, for the feed/dashboard badges; the schedules list
  // * itself is a query
  on(createExportScheduleSuccess, (state) => ({
    ...state,
    exportSchedulesCount: state.exportSchedulesCount + 1,
  })),

  on(deleteExportScheduleSuccess, (state) => ({
    ...state,
    exportSchedulesCount: Math.max(0, state.exportSchedulesCount - 1),
  })),

  on(
    saveObjectOutputSettingsStateSuccess,
    (state, { objectId, objectClassId, settingsType, settings }) => {
      if (
        !state.object ||
        state.object.object_id !== objectId ||
        state.object.object_class_id !== objectClassId
      ) {
        // The incoming object doesn't match the current state.object, so ignore
        return state;
      }

      return {
        ...state,
        object: {
          ...state.object,
          [settingsType]: settings,
        },
      };
    },
  ),

  on(share, (state, { exportTransportId }) => {
    return {
      ...state,
      isExporting: exportTransportId === NomExportTransportTypes.FILE,
    };
  }),

  on(shareCancelled, (state) => {
    return {
      ...state,
      isExporting: false,
    };
  }),
  on(shareSuccess, (state) => {
    return {
      ...state,
      isExporting: false,
    };
  }),

  on(shareFailure, (state) => {
    return {
      ...state,
      isExporting: false,
    };
  }),

  on(updateDashboardSuccess, (state, { dashboard }) => {
    if (!state.object || state.object.id !== dashboard.id) {
      return state;
    }

    // * preserve selected_page_id and other runtime state during Dashboard update
    const currentDashboard = state.object as Dashboard;

    return {
      ...state,
      object: {
        ...dashboard,
        selected_page_id: currentDashboard.selected_page_id,
      },
    };
  }),

  on(editFeedSuccess, (state, { feedId, feed }) => {
    // If state.object is null/undefined OR its id doesn't match, return state.
    if (!state.object || state.object.id !== feedId) {
      return state;
    }

    return {
      ...state,
      object: feed,
    };
  }),

  on(updateObjectViewProperties, (state, { viewProperties }) => {
    if (!state.object) {
      // No object loaded yet; nothing to update
      return state;
    }

    return {
      ...state,
      object: {
        ...state.object,
        view_properties: {
          ...state.object.view_properties,
          ...viewProperties,
        },
      },
    };
  }),

  // * Page-aware reducer handlers
  on(selectDashboardPage, (state, { pageId }) => {
    if (
      !state.object ||
      state.object.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return state;
    }

    const dashboard = state.object as Dashboard;
    const selectedPage = dashboard.pages.find((page) => page.id === pageId);

    if (!selectedPage) return state;

    return {
      ...state,
      object: {
        ...dashboard,
        selected_page_id: pageId,
      },
    };
  }),

  on(updateDashboardPagePeriod, (state, { pageId, period }) =>
    produce(state, (draft) => {
      if (
        !draft.object ||
        draft.object.object_class_id !== NomObjectClass.DASHBOARD
      ) {
        return;
      }

      const dashboard = draft.object as Dashboard;
      const pageIndex = dashboard.pages.findIndex((page) => page.id === pageId);

      if (pageIndex === -1) return;

      const page = dashboard.pages[pageIndex];
      page.period = period;
    }),
  ),

  // ! At the END of the wiring - revise toroughly the Reducer !!!
  on(createDashboardPageSuccess, (state, { page }) =>
    produce(state, (draft) => {
      if (
        !draft.object ||
        draft.object.object_class_id !== NomObjectClass.DASHBOARD
      ) {
        return;
      }

      const dashboard = draft.object as Dashboard;

      // * Use normalization service
      const normalizer = new PresentationNormalizerService();
      const normalizedSettings = normalizer.normalizeGlobalSettings(
        dashboard.presentation_settings,
      );

      // Add period fields to the page if not present, then normalize
      const pageWithPeriod = {
        ...page,
        period: page.period || {
          startTimestamp: startOfWeekTimestamp,
          endTimestamp: endOfTodayTimestamp,
        },
      };

      const enhancedPage = normalizer.normalizePage(
        pageWithPeriod,
        normalizedSettings,
      );

      dashboard.pages.push(enhancedPage);
    }),
  ),

  on(updateDashboardPageSuccess, (state, { page }) =>
    produce(state, (draft) => {
      if (
        !draft.object ||
        draft.object.object_class_id !== NomObjectClass.DASHBOARD
      ) {
        return;
      }

      const dashboard = draft.object as Dashboard;

      const pageIndex = dashboard.pages.findIndex((p) => p.id === page.id);

      if (pageIndex === -1) return;

      dashboard.pages[pageIndex] = page;

      // * Use normalization service to handle all presentation settings inheritance
      const normalizer = new PresentationNormalizerService();
      const normalized = normalizer.normalizeDashboard(dashboard);

      dashboard.pages = normalized.pages;
      dashboard.presentation_settings = normalized.presentation_settings;
    }),
  ),

  on(
    deleteDashboardPageSuccess,
    (state, { pages, deletedPageId, originalPages }) =>
      produce(state, (draft) => {
        if (
          !draft.object ||
          draft.object.object_class_id !== NomObjectClass.DASHBOARD
        ) {
          return;
        }

        const dashboard = draft.object as Dashboard;
        dashboard.pages = pages;

        // * Use normalization service to handle all presentation settings inheritance
        const normalizer = new PresentationNormalizerService();
        const normalized = normalizer.normalizeDashboard(dashboard);

        dashboard.pages = normalized.pages;
        dashboard.presentation_settings = normalized.presentation_settings;

        const currentSelectedPageId = dashboard.selected_page_id;

        // * deleted Page is the selected Page case
        if (currentSelectedPageId === deletedPageId) {
          const newSelectedPageId = findNextPageToSelect(
            // * use original pages for selection logic
            originalPages,
            deletedPageId,
            currentSelectedPageId,
          );

          if (newSelectedPageId) {
            dashboard.selected_page_id = newSelectedPageId;
          }
        }
      }),
  ),

  on(reorderDashboardPagesSuccess, (state, { pages }) =>
    produce(state, (draft) => {
      if (
        !draft.object ||
        draft.object.object_class_id !== NomObjectClass.DASHBOARD
      ) {
        return;
      }

      const dashboard = draft.object as Dashboard;

      // * Confirm the reorder with server response
      dashboard.pages = pages;
    }),
  ),

  on(reorderDashboardPagesFail, (state, { rollbackPages }) =>
    produce(state, (draft) => {
      if (
        !draft.object ||
        draft.object.object_class_id !== NomObjectClass.DASHBOARD
      ) {
        return;
      }

      const dashboard = draft.object as Dashboard;

      // * Rollback to original pages on failure
      dashboard.pages = rollbackPages;
    }),
  ),

  // * Page Multi-Select reducers
  on(setDashboardPagesSelection, (state, { pageIds }) => ({
    ...state,
    selectedDashboardPageIds: pageIds,
  })),

  on(
    toggleDashboardPageSelection,
    produce((draft, { pageId }) => {
      const index = draft.selectedDashboardPageIds.indexOf(pageId);
      if (index === -1) {
        draft.selectedDashboardPageIds.push(pageId);
      } else {
        draft.selectedDashboardPageIds.splice(index, 1);
      }
    }),
  ),

  on(clearDashboardPageSelection, (state) => ({
    ...state,
    selectedDashboardPageIds: [],
  })),
);

export function objectReducer(state: ObjectState | undefined, action: Action) {
  return _objectReducer(state, action);
}
